import resourceStrings from './ResourceStrings';
import { DropdownItem } from './components/ui/input/DropdownInput';

export const featureFlags = {
  enablePooling: true,
};

export interface HashMap<T> {
  [key: string]: T | undefined;
}

/**
 * Interface which describes a selectable time zone.
 */
export interface CellhireTimezone {
  readonly id: string;
  readonly name: string;
}

/**
 * List of available time zones which can be selected in the portal.
 */
export const timezoneList: DropdownItem[] = [
  { type: 'item', key: null, label: 'Browser Default' },
  {
    type: 'item',
    key: 'Europe/London',
    label: 'United Kingdom & Ireland (GMT)',
  },
  { type: 'item', key: 'Europe/Paris', label: 'Romance Standard Time' },
  { type: 'item', key: 'Pacific/Honolulu', label: 'Hawaiian Standard Time' },
  { type: 'item', key: 'America/Nome', label: 'Alaskan Standard Time' },
  { type: 'item', key: 'America/Los_Angeles', label: 'Pacific Standard Time' },
  { type: 'item', key: 'America/Denver', label: 'Mountain Standard Time' },
  { type: 'item', key: 'America/Chicago', label: 'Central Standard Time' },
  { type: 'item', key: 'America/New_York', label: 'Eastern Standard Time' },
  { type: 'item', key: 'America/Halifax', label: 'Atlantic Standard Time' },
  {
    type: 'item',
    key: 'America/St_Johns',
    label: 'Newfoundland Standard Time',
  },
  {
    type: 'item',
    key: 'America/Argentina/Buenos_Aires',
    label: 'Eastern South America Standard Time',
  },
  { type: 'item', key: 'America/Noronha', label: 'Mid-Atlantic Standard Time' },
  { type: 'item', key: 'Atlantic/Azores', label: 'Azores Standard Time' },
  { type: 'item', key: 'UTC', label: 'Coordinated Universal Time (UTC)' },
  { type: 'item', key: 'Europe/Madrid', label: 'Western Europe Standard Time' },
  { type: 'item', key: 'Europe/Kiev', label: 'E. Europe Standard Time' },
  { type: 'item', key: 'Asia/Kuwait', label: 'Kuwait, Riyadh' },
  { type: 'item', key: 'Europe/Saratov', label: 'Russian Standard Time' },
  { type: 'item', key: 'Asia/Dubai', label: 'Arabian Standard Time' },
  { type: 'item', key: 'Asia/Kabul', label: 'Afghanistan Standard Time' },
  { type: 'item', key: 'Asia/Ashgabat', label: 'Pakistan Standard Time' },
  { type: 'item', key: 'Asia/Calcutta', label: 'India Standard Time' },
  { type: 'item', key: 'Asia/Urumqi', label: 'Central Asia Standard Time' },
  {
    type: 'item',
    key: 'Asia/Pontianak',
    label: 'South East Asia Standard Time',
  },
  { type: 'item', key: 'Asia/Shanghai', label: 'China Standard Time' },
  { type: 'item', key: 'Asia/Tokyo', label: 'Tokyo Standard Time' },
  {
    type: 'item',
    key: 'Australia/Broken_Hill',
    label: 'Central Australia Standard Time',
  },
  {
    type: 'item',
    key: 'Australia/Sydney',
    label: 'Eastern Australia Standard Time',
  },
  {
    type: 'item',
    key: 'Pacific/Guadalcanal',
    label: 'Central Pacific Standard Time',
  },
  {
    type: 'item',
    key: 'Antarctica/McMurdo',
    label: 'New Zealand Standard Time',
  },
];

export const defaultViewList: DropdownItem[] = [
  { type: 'item', key: 'Dashboard', label: 'Dashboard' },
  { type: 'item', key: 'SimList', label: 'Sim List' },
];

export interface TimezoneLocales {
  locale: string;
  timezones: string[] | null;
}

const localeList = Array<TimezoneLocales>(
  {
    locale: 'en-gb',
    timezones: ['Europe/London'],
  },
  {
    locale: 'fr-FR',
    timezones: ['Europe/Paris'],
  },
  {
    locale: 'en-us',
    timezones: [
      'Pacific/Honolulu',
      'America/Nome',
      'America/Los_Angeles',
      'America/Denver',
      'America/Chicago',
      'America/New_York',
      'America/Halifax',
      'America/St_Johns',
      'America/Argentina/Buenos_Aires',
      'America/Noronha',
    ],
  },
  {
    locale: 'en',
    timezones: null,
  }
);

export const getLocale = (timezone: string) => {
  timezone = 'somethingelse';
  const rtnVal = localeList.filter((f) => f.timezones !== null && f.timezones.filter((a) => a === timezone).length > 0);

  return (rtnVal.length > 0 ? rtnVal[0] : localeList.filter((f) => f.timezones === null)[0]).locale;
};

export interface RnPara {
  type: 'para';
  text: string;
}

export interface RnTitle {
  type: 'title';
  text: string;
}

export interface RnSubheading {
  type: 'subheading';
  text: string;
}

export interface RnList {
  type: 'list';
  items: string[];
}

export type ReleaseNoteItem = RnTitle | RnSubheading | RnPara | RnList;

export interface ReleaseNote {
  id: number;
  userGuideVersion: string;
  dateString: string;
  items: ReleaseNoteItem[];
}

// add release notes as necessary, in descending date order, with descending id.
// add a corresponding entry to the sm.introduction table so that we can register when the user has seen it
export const releaseNotes: ReleaseNote[] = [
  {
    id: -5,
    userGuideVersion: '1.62',
    dateString: 'Monday 3 October 2022',
    items: [
      { type: 'title', text: 'Updated Look and Feel' },
      {
        type: 'list',
        items: [
          'The sim list look and feel has been updated',
          "The sim attributes page has been replaced with a new 'slide-out' panel, available via the 'i' button",
          'Overall sim loading times have been improved',
        ],
      },
    ],
  },
  {
    id: -4,
    userGuideVersion: '1.62',
    dateString: 'Tuesday 18 May 2021',
    items: [
      { type: 'title', text: 'Change Account' },
      {
        type: 'list',
        items: [
          'The "Change Account" command is now available from the SIM Cards page.',
          'This functionality will allow you to move SIMs from one account to another.',
          'This functionality is only intended for use with non-provisioned SIMs.',
          'This functionality will only be available to those companies with more than ' + 'one account.',
          'Please request access to this command if it will be useful to you. ' +
            'This option will not be visible if you do not have access.',
        ],
      },
    ],
  },
  {
    id: -3,
    userGuideVersion: '1.60',
    dateString: 'Wednesday 17 March 2021',
    items: [
      { type: 'title', text: 'Provisioning Orders' },
      {
        type: 'list',
        items: [
          '"Provisioning Orders" are now used to group together a set of connections.',
          'The SIMs list shows the provisioning order to which a connection is assigned.',
          'A provisioning order can be used to identify the scope of a pool or sharer group or, ' +
            'optionally, it can be used simply to group together SIMs.',
          'When making a new connection, you will need to specify a Provisioning Order to which ' +
            'the connection will be assigned.',
          'The default provisioning order for your account and network provider will be indicated ' +
            'with an asterisk. Sometimes it may not be available for the particular tariff you ' +
            'have chosen.',
          'When changing the provisioning, you may now change the Provisioning ' +
            'Order to which the connection is assigned.',
          'It is possible to add a connection to an existing provisioning order, or ' + 'to create a new one.',
        ],
      },
    ],
  },
  {
    id: -2,
    userGuideVersion: '1.58',
    dateString: 'Friday 31 July 2020',
    items: [
      { type: 'title', text: 'Bundle Monitor Enhancements' },
      {
        type: 'list',
        items: ['Bundle Monitor defaults can now be administered at the company and account level.'],
      },
      { type: 'title', text: 'Minor Improvements' },
      {
        type: 'list',
        items: [
          'Submission completed notification emails now contain the description that the user' +
            ' entered for the submission.',
          'It is now possible to select text from within grids such as the list of SIMs.',
        ],
      },
    ],
  },
  {
    id: -1,
    userGuideVersion: '1.57',
    dateString: 'Saturday 11 July 2020',
    items: [
      { type: 'title', text: 'Bundle Monitor Functionality' },
      { type: 'subheading', text: 'What Is It?' },
      {
        type: 'para',
        text:
          'Bundle Monitor provides a flexible way to control end - user data consumption though' +
          ' email / SMS warnings and, for the main UK and French networks, barring actions.',
      },
      { type: 'subheading', text: 'What are the Key Benefits?' },
      {
        type: 'list',
        items: [
          'Alert levels can be defined differently per SIM.',
          'Options to send alert warnings via email or SMS at any number of defined levels.',
          'Option to send emails from your email domain with a white - labelled template and ability to add' +
            ' a custom SMS Alpha Tag.',
          'Options to bar at trigger levels on Voda UK, O2 UK and Orange France.',
        ],
      },
      {
        type: 'subheading',
        text: 'How Does this differ from SMS Alerting and Spend Capping?',
      },
      {
        type: 'list',
        items: [
          'Both SMS Alerting and Spend Capping rely on you billing the same tariff structure to' +
            ' the end user(bundle amount, overage) as you receive.',
          'Spend Capping is only supported in the UK for UK based entities for UK networks.',
          'Spend Capping is a bill spend control mechanism around all types of usage(voice, data, SMS)' +
            ' and results in invoice credits. Bundle Monitor is a data alerting control that aims to curtail' +
            ' excess usage working on best endeavours but does not result in invoice credits.',
        ],
      },
      { type: 'subheading', text: 'How Do I Get This?' },
      {
        type: 'list',
        items: [
          'Contact your account manager.',
          'We will give you access to the new feature and work with you to set up the essential configuration' +
            ' for this feature (e.g default messages, email templates).',
        ],
      },
    ],
  },
];

export const latestReleaseId = (releaseNotes.length && releaseNotes[0].id) || undefined;

export const validationErrorTypes = [
  {
    errorTypes: ['sourceNetworkError'],
    displayText: resourceStrings.sourceNetwork,
  },
  {
    errorTypes: ['serialNumberError', 'serialNumber'],
    displayText: resourceStrings.serialNumber,
  },
  { errorTypes: ['pacError'], displayText: resourceStrings.pacCode },
  {
    errorTypes: ['migratedInSimTypeError'],
    displayText: resourceStrings.migratedInSimType,
  },
  { errorTypes: ['mobileNumber'], displayText: resourceStrings.mobileNumber },
  {
    errorTypes: ['connectionDateError'],
    displayText: resourceStrings.connectionDate,
  },
  {
    errorTypes: ['provisionTypeError'],
    displayText: resourceStrings.provisionType,
  },
];

export const SmsRegex = /^[A-Za-z0-9\-_&' ]+$/;
